import axios from 'axios';
import { getEnvironmentConfig } from './../utils/getEnvironmentConfig';

let refreshing = false;
let refreshPromise: Promise<void> | null = null;
let lastRefreshTime = 0;

const getTokens = () => {
	return {
		accessToken: sessionStorage.getItem('accessToken'),
		refreshToken: sessionStorage.getItem('refreshToken'),
		refreshExpires: sessionStorage.getItem('refreshExpires'),
	};
};

const resetTokens = (accessToken: string, refreshToken: string, refreshExpires: string) => {
	// console.log('Setting new tokens:', { accessToken, refreshToken, refreshExpires });
	sessionStorage.setItem("accessToken", accessToken);
	sessionStorage.setItem("refreshToken", refreshToken);
	sessionStorage.setItem("refreshExpires", refreshExpires);
};

function decodeJWT(token: string) {
	try {
		const payload = token.split('.')[1];
		const decoded = atob(payload); // base64 decode
		let parsed = JSON.parse(decoded);
		return parsed;
	} catch (err) {
		console.error('Error decoding JWT:', err);
		return null;
	}
}

const refreshAccessToken = async (): Promise<void> => {
	// Prevent multiple refresh attempts within 5 seconds
	const now = Date.now();
	if (now - lastRefreshTime < 5000) {
		console.log('Skipping refresh - too soon since last refresh');
		return;
	}

	// If a refresh is already in progress, return that promise
	if (refreshing && refreshPromise) {
		console.log('Refresh already in progress, returning existing promise');
		return refreshPromise;
	}

	refreshing = true;
	lastRefreshTime = now;
	const { refreshToken, refreshExpires, accessToken } = getTokens();

	console.log('Starting token refresh with:', { 
		hasRefreshToken: !!refreshToken,
		hasAccessToken: !!accessToken,
		refreshExpires
	});

	if (!refreshToken) {
		refreshing = false;
		throw new Error("No refresh token available");
	}


	try {
		const response = await client.post('/subsidiary/dashboard/refresh/token', null, {
			headers: {
				"Authorization": `Bearer ${accessToken}`,
				"x-refresh-token": refreshToken
			}
		});

		console.log('Refresh token response:', response.data);
		
		if (response.data?.token) {
			resetTokens(
				response.data.token.accessToken,
				response.data.token.refreshToken,
				response.data.token.refreshExpires
			);
			console.log('Tokens updated successfully');
		} else {
			console.error('Invalid refresh token response format:', response.data);
			throw new Error("Invalid refresh token response");
		}
	} catch (err: any) {
		console.log(err);
		
		console.error("Failed to refresh access token:", err.response?.data || err.message);
		if (err.response?.status === 401) {
			console.log('Refresh token failed with 401, redirecting to login');
			window.location.href = '/login';
		}
		
		throw err;
	} finally {
		refreshing = false;
		refreshPromise = null;
	}
};

const client = axios.create({
	baseURL: getEnvironmentConfig("ROOTURL"),
	headers: {
		'content-type': 'application/json'
	},
});

// 🔁 Request interceptor with proactive refresh logic
client.interceptors.request.use(
	async (config) => {
		const accessToken = sessionStorage.getItem('accessToken');

		if (accessToken) {
			try {
				const decodedJwt = decodeJWT(accessToken);
				if (!decodedJwt) {
					console.log('Invalid JWT, skipping refresh check');
					return config;
				}

				const currentTime = Math.floor(Date.now() / 1000);
				const tokenExpiresIn = decodedJwt.exp - currentTime;

				// // ADD THIS LINE DURING DEVELOPMENT TO SIMULATE
				// const SIMULATE_EXPIRY = false; 
				// const effectiveExpiresIn = SIMULATE_EXPIRY ? 59 : tokenExpiresIn;


				// Refresh if token expires in 60 seconds and not already refreshing
				if (tokenExpiresIn <= 60 && !refreshing) {
					try {
						await refreshAccessToken();
					} catch (err: any) {
						console.error("Proactive token refresh failed:", err.response?.data || err.message);
						if (err.response?.status === 401) {
							window.location.href = '/login';
						}
						return Promise.reject(err);
					}
				}
			} catch (err) {
				console.error("Error in request interceptor:", err);
			}
		}

		const newAccessToken = sessionStorage.getItem('accessToken');
		if (newAccessToken) {
			config.headers["Authorization"] = `Bearer ${newAccessToken}`;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

// Response interceptor
client.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		if (error.response?.status === 401 && !originalRequest._retry) {
			console.log('401 error detected, attempting token refresh');
			originalRequest._retry = true;

			try {
				
				const accessToken = sessionStorage.getItem('accessToken');
				if (accessToken) {
					originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
					return client(originalRequest);
				}
			} catch (refreshError: any) {
				console.error("Token refresh failed in response interceptor:", refreshError.response?.data || refreshError.message);
				if (refreshError.response?.status === 401) {
					window.location.href = '/login';
				}
				return Promise.reject(refreshError);
			}
		}

		if (error?.response?.data?.message === 'Please login again') {
			window.location.href = '/login';
		}
		return Promise.reject(error);
	}
);

// Apply the same interceptors to formClient
export const formClient = axios.create({
	baseURL: getEnvironmentConfig("ROOTURL"),
	headers: {
		'content-type': 'application/json',
	},
});

formClient.interceptors.request.use(
	async (config) => {
		const accessToken = sessionStorage.getItem('accessToken');

		if (accessToken) {
			try {
				const decodedJwt = decodeJWT(accessToken);
				if (!decodedJwt) {
					return config;
				}

				const currentTime = Math.floor(Date.now() / 1000);
				const tokenExpiresIn = decodedJwt.exp - currentTime;

				// const SIMULATE_EXPIRY = false; // set to false in production
				// const effectiveExpiresIn = SIMULATE_EXPIRY ? 50 : tokenExpiresIn;

				if (tokenExpiresIn <= 60 && !refreshing) {
					try {
						await refreshAccessToken();
					} catch (err: any) {
						console.error("Proactive token refresh failed (formClient):", err.response?.data || err.message);
						if (err.response?.status === 401) {
							window.location.href = '/login';
						}
						return Promise.reject(err);
					}
				}
			} catch (err) {
				console.error("Error in formClient request interceptor:", err);
			}
		}

		const newAccessToken = sessionStorage.getItem('accessToken');
		if (newAccessToken) {
			config.headers["Authorization"] = `Bearer ${newAccessToken}`;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

formClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			try {
				
				const accessToken = sessionStorage.getItem('accessToken');
				if (accessToken) {
					originalRequest.headers.authorization = `Bearer ${accessToken}`;
					return formClient(originalRequest);
				}
			} catch (refreshError: any) {
				if (refreshError.response?.status === 401) {
					window.location.href = '/login';
				}
				return Promise.reject(refreshError);
			}
		}

		if (error?.response?.data?.message === 'Please login again') {
			window.location.href = '/login';
		}
		return Promise.reject(error);
	}
);

export default client;
